import * as React from "react";
import { useCallback, useContext, useLayoutEffect, useRef } from "react";
import flatten from "lodash/flatten";
import isEqual from "lodash/isEqual";
import styled, { css } from "styled-components";
import Immutable from "immutable";
import Field from "views/components/Field";
import Value from "views/components/Value";
import fieldTypeFor from "views/logic/fieldtypes/FieldTypeFor";
import SortConfig from "views/logic/aggregationbuilder/SortConfig";
import { Icon } from "components/common";
import InteractiveContext from "views/components/contexts/InteractiveContext";
import FieldSortIcon from "./FieldSortIcon";
import TableHeaderCell from "./TableHeaderCell";
const CenteredTh = styled(TableHeaderCell)`
  text-align: center;
`;
const PinIcon = styled.button(
  ({ theme }) => css`
    display: inline-flex;
    border: 0;
    background: transparent;
    padding: 5px;
    cursor: pointer;
    position: relative;
    color: ${theme.colors.gray[70]};
    vertical-align: middle;

    &.active {
      color: ${theme.colors.gray[20]};
    }
  `
);
const HeaderField = ({
  borderedHeader,
  fields,
  field,
  prefix = "",
  span = 1,
  title = field,
  onSortChange,
  sortConfigMap,
  sortable,
  sortType = void 0,
  onSetColumnsWidth = void 0,
  isPinned = void 0,
  showPinIcon = false,
  togglePin,
  setLoadingState
}) => {
  const type = fieldTypeFor(field, fields);
  const thRef = useRef(null);
  useLayoutEffect(() => {
    var _a;
    if (onSetColumnsWidth && ((_a = thRef == null ? void 0 : thRef.current) == null ? void 0 : _a.offsetWidth)) {
      onSetColumnsWidth({ field: `${prefix}${field}`, offsetWidth: thRef.current.offsetWidth });
    }
  }, [onSetColumnsWidth, field, prefix]);
  const _togglePin = useCallback(() => {
    togglePin(`${prefix}${field}`);
  }, [togglePin, prefix, field]);
  return /* @__PURE__ */ React.createElement(
    TableHeaderCell,
    {
      ref: thRef,
      key: `${prefix}${field}`,
      colSpan: span,
      $isNumeric: type.isNumeric(),
      $borderedHeader: borderedHeader
    },
    /* @__PURE__ */ React.createElement(Field, { name: field, type }, title),
    showPinIcon && /* @__PURE__ */ React.createElement(
      PinIcon,
      {
        "data-testid": `pin-${prefix}${field}`,
        type: "button",
        onClick: _togglePin,
        className: isPinned ? "active" : ""
      },
      /* @__PURE__ */ React.createElement(Icon, { name: "keep" })
    ),
    sortable && sortType && /* @__PURE__ */ React.createElement(
      FieldSortIcon,
      {
        fieldName: field,
        onSortChange,
        setLoadingState,
        sortConfigMap,
        type: sortType
      }
    )
  );
};
const HeaderFieldForValue = ({
  field,
  value,
  span = 1,
  prefix = "",
  type,
  borderedHeader
}) => /* @__PURE__ */ React.createElement(CenteredTh, { key: `${prefix}${field}-${value}`, colSpan: span, $borderedHeader: borderedHeader }, /* @__PURE__ */ React.createElement(Value, { field, value, type }));
const Spacer = ({ span }) => /* @__PURE__ */ React.createElement("th", { "aria-label": "spacer", colSpan: span });
const RowNumberHeader = styled(TableHeaderCell)(
  ({ theme }) => css`
    && {
      width: 20px;
      min-width: 20px;
      max-width: 200px;
      white-space: nowrap;
      color: ${theme.colors.text.secondary};
    }
  `
);
const ColumnPivotFieldsHeaders = ({
  fields,
  pivots,
  values,
  series,
  offset = 1,
  borderedHeader
}) => {
  const headerRows = pivots.map((columnPivot, idx) => {
    const actualValues = values.map((key) => ({ path: key.slice(0, idx).join("-"), key: key[idx] || "", count: 1 }));
    const actualValuesWithoutDuplicates = actualValues.reduce((prev, cur) => {
      const lastItem = prev == null ? void 0 : prev.at(-1);
      const lastKey = lastItem == null ? void 0 : lastItem.key;
      const lastPath = lastItem == null ? void 0 : lastItem.path;
      if (lastKey === cur.key && isEqual(lastPath, cur.path)) {
        const remainder = prev.slice(0, -1);
        const newLastItem = { ...lastItem, count: lastItem.count + 1 };
        return [...remainder, newLastItem];
      }
      return [...prev, cur];
    }, []);
    const type = fieldTypeFor(columnPivot, fields);
    return /* @__PURE__ */ React.createElement("tr", { key: `header-table-row-${columnPivot}` }, offset > 0 && /* @__PURE__ */ React.createElement(Spacer, { span: offset }), actualValuesWithoutDuplicates.map((value) => /* @__PURE__ */ React.createElement(
      HeaderFieldForValue,
      {
        key: `header-field-value-${value.path}-${value.key}`,
        borderedHeader,
        field: columnPivot,
        value: value.key,
        span: value.count * series.length,
        prefix: value.path,
        type
      }
    )));
  });
  return /* @__PURE__ */ React.createElement(React.Fragment, null, headerRows);
};
const Headers = ({
  borderedHeader,
  columnPivots,
  fields,
  rowPivots,
  series,
  rollup,
  actualColumnPivotFields,
  onSortChange,
  sortConfigMap,
  onSetColumnsWidth = void 0,
  pinnedColumns = Immutable.Set(),
  togglePin,
  setLoadingState,
  showRowNumbers = true
}) => {
  const rowFieldNames = rowPivots.flatMap((pivot) => pivot.fields);
  const columnFieldNames = columnPivots.flatMap((pivot) => pivot.fields);
  const interactive = useContext(InteractiveContext);
  const headerField = ({
    field,
    prefix = "",
    span = 1,
    title = field,
    sortable = false,
    sortType = void 0,
    showPinIcon = false
  }) => /* @__PURE__ */ React.createElement(
    HeaderField,
    {
      borderedHeader,
      key: `${prefix}${field}`,
      fields,
      field,
      prefix,
      span,
      title,
      onSortChange,
      sortConfigMap,
      sortable,
      sortType,
      onSetColumnsWidth,
      isPinned: pinnedColumns.has(`${prefix}${field}`),
      showPinIcon,
      togglePin,
      setLoadingState
    }
  );
  const rowPivotFields = rowFieldNames.map(
    (fieldName) => headerField({ field: fieldName, sortable: interactive, sortType: SortConfig.PIVOT_TYPE, showPinIcon: interactive })
  );
  const seriesFields = series.map(
    (s) => headerField({
      field: s.function,
      prefix: "",
      span: 1,
      title: s.effectiveName,
      sortable: interactive,
      sortType: SortConfig.SERIES_TYPE,
      showPinIcon: false
    })
  );
  const columnPivotFields = flatten(
    actualColumnPivotFields.map(
      (key) => series.map(
        (s) => headerField({
          field: s.function,
          prefix: key.join("-"),
          span: 1,
          title: s.effectiveName,
          sortable: false,
          showPinIcon: false
        })
      )
    )
  );
  const offset = (rollup ? rowFieldNames.length + series.length : rowFieldNames.length) + (showRowNumbers ? 1 : 0);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    ColumnPivotFieldsHeaders,
    {
      borderedHeader,
      fields,
      pivots: columnFieldNames,
      values: actualColumnPivotFields,
      series,
      offset
    }
  ), /* @__PURE__ */ React.createElement("tr", { className: "pivot-header-row" }, showRowNumbers && /* @__PURE__ */ React.createElement(RowNumberHeader, { $isNumeric: true }, "#"), rowPivotFields, rollup && seriesFields, columnPivotFields));
};
export default Headers;
